// Vendor globals.
//
// The application still refers to these as globals - Vue, VueRouter, firebase,
// bootstrap, bb, jspdf, axios - exactly as it did when they arrived via CDN
// <script> tags. Importing them here pins every version in package-lock.json and
// bundles them into the build, without touching ~9,000 lines of call sites.
// Replacing the global references with direct imports is a later Phase 1 step.
//
// Google Maps deliberately stays a CDN <script> in index.html: it is a loader
// keyed by an API key, not a package.

import * as Vue from 'vue';
import * as VueRouter from 'vue-router';

// Firebase stays on the v9 COMPAT surface - firebase.database(), firebase.auth()
// and friends - because migrating to the modular SDK is its own change. Pinned to
// the same 9.10.0 the CDN tags served, so this commit changes packaging only.
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import 'firebase/compat/storage';
import 'firebase/compat/functions';

import * as bootstrap from 'bootstrap';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

window.Vue = Vue;
window.VueRouter = VueRouter;
window.firebase = firebase;
window.bootstrap = bootstrap;

// Loaded on demand, not at startup. These were the four heaviest things in the
// bundle and none of them is needed to render a page:
//   billboard.js  ~1.1 MB of source, used by exactly one chart
//   jspdf         ~0.9 MB with html2canvas and canvg, used only to make a PDF
//   firestore     ~0.8 MB, read only by the receipt-OCR listener
// Each loader caches on window, so repeat calls do not re-import.
export async function loadJsPDF() {
  if (!window.jspdf) {
    const { jsPDF } = await import('jspdf');
    window.jspdf = { jsPDF };
  }
  return window.jspdf.jsPDF;
}

export async function loadBillboard() {
  if (!window.bb) {
    const [billboard] = await Promise.all([
      import('billboard.js'),
      import('billboard.js/dist/billboard.css'),
    ]);
    window.bb = billboard.bb || billboard.default || billboard;
  }
  return window.bb;
}

// Messaging is opt-in: most sessions never enable notifications, so it is not
// worth ~99 KB in the entry chunk. Loaded when the user turns notifications on,
// or on startup for someone who has already granted permission.
export async function loadMessaging() {
  if (!firebase.messaging) {
    await import('firebase/compat/messaging');
  }
  return firebase.messaging();
}

// Only needed when someone actually shows a payment QR code. Lazy, like the
// others, so it costs nothing on a page that never displays one.
export async function loadQrCode() {
  const mod = await import('qrcode');
  return mod.default || mod;
}

export async function loadFirestore() {
  if (!firebase.firestore) {
    await import('firebase/compat/firestore');
  }
  return firebase.firestore();
}
